import React from 'react';
import css from './graph.module.css';
import chartArrow from './sheeps/icons/chart-arrow.svg';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Scatter,
  ComposedChart,
  ResponsiveContainer,
  Bar,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

type DataObject = {
  dataKey: string;
  dataTitle?: string;
  strokeColor: string;
  yAxis: 'left' | 'right' | 'third';
};

type GraphProps = {
  title: string;
  height?: number;
  xKey?: string;
  xTitle?: string;
  yLeftValue?: string;
  yRightValue?: string;
  dataKeyScatter?: string;
  dataTitleScatter?: string;
  goal?: number;
  data: Array<any>;
  dataObjects?: DataObject[];
  barObjects?: DataObject[];
  yAxisGoal?: string;
  isExpandable?: boolean;
  allowDecimal?: boolean;
};

export default function SummaryGraph({
  title,
  height = 193,
  xKey,
  xTitle,
  yLeftValue = '',
  yRightValue = '',
  yAxisGoal = 'right',
  dataKeyScatter = '',
  dataTitleScatter = '',
  goal = null,
  data = [],
  dataObjects = [],
  barObjects = [],
  isExpandable = false,
  allowDecimal = false,
}: GraphProps) {

  const { t } = useTranslation();

  const xDefaultTitle = xTitle ?? t('pages.livestock.summary.view.months');

  const dateFormat = (value: any): string => {
    return moment(value, moment.ISO_8601, true).isValid() ? moment(value).format('MM.YYYY') : value;
  }

  // Better scaling for data lines
  // Scales stuff for each y axis
  const leftAxisData = dataObjects.filter(obj => obj.yAxis === 'left');
  const rightAxisData = dataObjects.filter(obj => obj.yAxis === 'right');
  const leftAxisBarData = barObjects.filter(obj => obj.yAxis === 'left');

  const calculateMinMax = (axisData) => {
    if (axisData.length === 0) return [0, 0];
    const allValues = data.reduce((acc, curr) => {
      const values = axisData.map(obj => curr[obj.dataKey]);
      return acc.concat(values);
    }, []);
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    return [minValue, maxValue];
  };

  const [minLeftBar, maxLeftBar] = calculateMinMax(leftAxisBarData);
  const [minLeft, maxLeft] = calculateMinMax(leftAxisData);
  const [minRight, maxRight] = calculateMinMax(rightAxisData);

  const goalData = data.map(item => ({
    [xKey]: item[xKey],
    goal: goal !== null ? goal : 0
  }));

  return (
    <div className={css['graph-container']}>
      <div className={css['title']}>
        <p>{title}</p>
        {isExpandable && (
          <img src={chartArrow} className={css['arrow-icon']} alt="" />
        )}
      </div>
      {data?.length > 0 ? (
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart
            data={data}
            margin={{
              top: 0,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={xKey}
              tickFormatter={dateFormat}
              label={{
                value: xDefaultTitle,
                position: 'insideBottom',
                offset: -10,
              }}
            />
            <YAxis
              yAxisId="left"
              label={{
                value: yLeftValue,
                style: { textAnchor: 'middle' },
                angle: -90,
                position: 'left',
                offset: 0,
              }}
              domain={[minLeft, maxLeft]}
              tickFormatter={(value) => allowDecimal ? value : Math.round(value).toString()}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              label={{
                value: yRightValue,
                style: { textAnchor: 'middle' },
                angle: -90,
                position: 'right',
                offset: 0,
              }}
              domain={minRight && maxRight ? [minRight, maxRight] : null}
              tickFormatter={(value) => allowDecimal ? value : Math.round(value).toString()}
            />
            <YAxis
              yAxisId="third"
              domain={[minLeftBar, maxLeftBar]}
              hide={true}
            />
            <Tooltip labelFormatter={dateFormat} />

            {dataObjects.map(({ dataKey, dataTitle, strokeColor, yAxis }) => (
              <Line
                key={dataKey}
                name={dataTitle}
                type="linear"
                dataKey={dataKey}
                stroke={strokeColor}
                activeDot={{ r: 8 }}
                yAxisId={yAxis}
              />
            ))}

            {dataKeyScatter && (
              <Scatter
                name={dataTitleScatter}
                dataKey={dataKeyScatter}
                fill="#A97E81"
                yAxisId="right"
                legendType="circle"
              />
            )}

            {barObjects.map(({ dataKey, dataTitle, strokeColor, yAxis }) => (
              <Bar
                key={dataKey}
                name={dataTitle}
                dataKey={dataKey}
                fill={strokeColor}
                yAxisId={yAxis}
              />
            ))}

            {goal && (
              <Line
                type="linear"
                dataKey="goal"
                stroke="#50AE2F"
                name={t('pages.livestock.summary.view.goal_dry_period')}
                strokeWidth={1}
                yAxisId={yAxisGoal}
                dot={false}
                connectNulls
                data={goalData}
              />
            )}

            <Legend verticalAlign="top" align="right" height={25} wrapperStyle={{ left: -30 }} />

          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <p>{t('pages.livestock.summary.section.no_data_available')}</p>
      )}
    </div>
  );
}
